import ChooseModelSignatureModal from "@/components/core/modals/ChooseModelSignatureModal";
import Recipient from "@/components/core/subComponents/Recipient";
import {
  setCurrentTransactionField,
  setTransactionSignatures,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { ModelMail } from "@/type/core/models/models.types";
import useMessageSection from "@/utils/hooks/useMessageSection";
import useRecipient from "@/utils/hooks/useRecipient";
import SubmitButton from "@components/core/buttons/SubmitButton";
import InputFormField from "@components/core/inputs/InputFormField";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import SignatureMailForm from "@components/core/subComponents/SignatureMailForm";
import { formValidateTriggers } from "@utils/Constant";
import { Col, Form, Row } from "antd";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignatureMessageSectionProps {
  officeId?: string;
  isFromModel?: boolean;
  className?: string;
  isLoading?: boolean;
  invitation?: ModelMail;
  endOfTransaction?: ModelMail;
  isSignaturesFromModel?: boolean;
}

const SignatureMessageSection: FunctionComponent<
  SignatureMessageSectionProps
> = ({
  officeId,
  isFromModel = false,
  className = "",
  isLoading = false,
  invitation,
  endOfTransaction,
  isSignaturesFromModel,
}) => {
  const { t } = useTranslation();

  const [isEndTransactionMailChanged, setIsEndTransactionMailChanged] =
    useState(false);

  const { signatories, signatures, files } = useCurrentTransaction();

  const {
    office,
    contacts,
    members,
    extraRecipients,
    setExtraRecipients,
    handleAddMe,
  } = useRecipient(officeId);

  const {
    form,
    invitationSignatureInitialValue,
    setInvitationSignatureInitialValue,
    invitationMailForm,
    invitationMailFormValues,
    endOfTransactionSignatureInitialValue,
    setEndOfTransactionSignatureInitialValue,
    endOfTransactionMailForm,
    endOfTransactionMailFormValues,
    submittable,
    initialValues,
  } = useMessageSection({ invitation, endOfTransaction, isFromModel });

  const [
    isRecapSignatoriesWithCertificateOpen,
    setIsRecapSignatoriesWithCertificateOpen,
  ] = useState<boolean>(false);
  const [isChooseModelSignatureModalOpen, setIsChooseModelSignatureModalOpen] =
    useState<boolean>(false);

  const submitForm = () => {
    if (isSignaturesFromModel) {
      setIsChooseModelSignatureModalOpen(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const reference: string = form.getFieldValue("object");

    const invitationModelMail: ModelMail = {
      ...invitation,
      mailConfiguration: {
        ...invitationMailFormValues,
      },
      defaultModel: invitationMailFormValues.defaultModel,
    };
    const endOfTransactionModelMail: ModelMail = {
      ...endOfTransaction,
      mailConfiguration: {
        ...endOfTransactionMailFormValues,
      },
      defaultModel: endOfTransactionMailFormValues.defaultModel,
    };

    setCurrentTransactionField([
      {
        field: "reference",
        value: reference,
      },
      {
        field: "endOfTransaction",
        value: isEndTransactionMailChanged
          ? endOfTransactionModelMail
          : undefined,
      },
      {
        field: "invitation",
        value: invitationModelMail,
      },
      {
        field: "endOfTransactionModelMailId",
        value: endOfTransaction?.id,
      },
    ]);
    setTransactionSignatures(
      signatures.filter((signature) => signature.email != undefined),
    );
  };

  return (
    <Row
      className={`app-section-message-signature justify-content-center ${className}`}
    >
      <Col span={24} lg={20} xl={16}>
        <Form
          form={form}
          onFinish={submitForm}
          initialValues={initialValues}
          {...formValidateTriggers}
          layout="vertical"
        >
          <InputFormField
            showLabel
            showTooltip
            labelTooltip="transactionReference"
            module={
              isFromModel
                ? "offices.transactionModel"
                : "signature.accordion.parametersForm"
            }
            field="object"
            maxLength={45}
            labelClassName="label-tertiary"
          />
          {!isFromModel && (
            <>
              {/* TODO: Temporary fix to initialize form => Refacto by using single form at root level */}
              <Form
                form={invitationMailForm}
                {...formValidateTriggers}
                layout="vertical"
              >
                <SignatureMailForm
                  form={invitationMailForm}
                  mailType={"invitation"}
                  officeId={officeId}
                  transactionType="signature"
                  invitationMailName={invitation?.name}
                  invitationMailOfficeOrUserName={invitation?.officeOrUserName}
                  signatureInitialValue={invitationSignatureInitialValue}
                  setSignatureInitialValue={setInvitationSignatureInitialValue}
                />
              </Form>
              {/* TODO: Temporary fix to initialize form => Refacto by using single form at root level */}
              <Form
                form={endOfTransactionMailForm}
                {...formValidateTriggers}
                layout="vertical"
              >
                <SignatureMailForm
                  className="border-top-secondary pt-24"
                  form={endOfTransactionMailForm}
                  mailType={"endOfTransaction"}
                  officeId={officeId}
                  transactionType="signature"
                  endOfTransactionMailName={endOfTransaction?.name}
                  endOfTransactionMailOfficeOrUserName={
                    endOfTransaction?.officeOrUserName
                  }
                  signatureInitialValue={endOfTransactionSignatureInitialValue}
                  setSignatureInitialValue={
                    setEndOfTransactionSignatureInitialValue
                  }
                  triggerFormChanged={() =>
                    setIsEndTransactionMailChanged(true)
                  }
                />
              </Form>
            </>
          )}
          {office && (
            <Recipient
              className="mt-48 border-top-secondary pt-24"
              office={office}
              onAddMe={handleAddMe}
              contacts={contacts}
              members={members}
              recipients={extraRecipients}
              setRecipients={setExtraRecipients}
            />
          )}

          {!isFromModel && (
            <div className="d-flex align-items-center justify-content-end flex-wrap gap-8 pt-30">
              <SubmitButton
                disabled={!submittable}
                isSubmitting={isLoading}
                dataAlt={t("signature.accordion.last.validate")}
                label={"signature.accordion.last.validate"}
                className="mw-160"
                title={
                  !submittable
                    ? t("signature.accordion.last.missingObject")
                    : undefined
                }
              />
            </div>
          )}
        </Form>
      </Col>
      <ConfirmModal
        visible={isRecapSignatoriesWithCertificateOpen}
        onCancel={() => setIsRecapSignatoriesWithCertificateOpen(false)}
        onOk={() => {
          setIsRecapSignatoriesWithCertificateOpen(false);
          handleSubmit();
        }}
        title={t("transaction.detail.recapSignatoriesWithCertificate.title")}
        content={
          <span>
            {t("transaction.detail.recapSignatoriesWithCertificate.body") + " "}
            {signatories
              .filter(
                (signatoryWithCertificate) =>
                  signatoryWithCertificate.withCertificate === true &&
                  signatoryWithCertificate.certificate == null,
              )
              .map(
                (signatory) => `${signatory.lastname} ${signatory.firstname}`,
              )
              .join(", ")}
          </span>
        }
      />
      {isSignaturesFromModel && (
        <ChooseModelSignatureModal
          visible={isChooseModelSignatureModalOpen}
          onCancel={() => setIsChooseModelSignatureModalOpen(false)}
          onOk={() => {
            setIsChooseModelSignatureModalOpen(false);
            handleSubmit();
          }}
          signatories={signatories}
          signaturesFromModel={signatures}
          firstFileFromModelName={files.length > 0 ? files[0].name : undefined}
        />
      )}
    </Row>
  );
};

export default SignatureMessageSection;
